import { useController } from "react-hook-form";
import { Plus } from "lucide-react";
import type { Lens } from "@hookform/lenses";
import type { CustomPresetData, CustomSize } from "../../model/transferLabTypes";
import { createCustomSize } from "../../model/formHelpers";
import { CustomSizeRow } from "./CustomSizeRow";
import { CollapsableForm } from "./CollapsableForm";

export type CustomPresetFormProps = {
  customPresetId: string;
  customPresetLens: Lens<CustomPresetData>;
  // Props for pricing
  artworkId?: string;
  transferTypeId?: string;
  enhancementTier?: string;
  aspectRatio?: number | undefined;
  bounds?:
    | {
        minWidthInches: number;
        minHeightInches: number;
        maxWidthInches: number;
        maxHeightInches: number;
      }
    | undefined;
};

export function CustomPresetForm({
  customPresetId,
  customPresetLens,
  artworkId,
  transferTypeId,
  enhancementTier,
  aspectRatio,
  bounds
}: CustomPresetFormProps) {
  // Get custom preset data using controller with lens
  const { field } = useController(customPresetLens.interop());

  if (!field.value) return null;

  const { customSizes }: { customSizes: CustomSize[] } = field.value;

  const handleAddSize = () => {
    const newSize = createCustomSize(aspectRatio, bounds);
    field.onChange({
      ...field.value,
      customSizes: [...customSizes, newSize]
    });
  };

  const handleRemoveSize = (sizeId: string) => {
    if (customSizes.length <= 1) return; // Keep at least one size

    field.onChange({
      ...field.value,
      customSizes: customSizes.filter((s: CustomSize) => s.id !== sizeId)
    });
  };

  const handleSizeChange = (sizeId: string, updates: Partial<CustomSize>) => {
    field.onChange({
      ...field.value,
      customSizes: customSizes.map((size: CustomSize) =>
        size.id === sizeId ? { ...size, ...updates } : size
      )
    });
  };

  // Remove custom preset entirely
  const handleRemove = () => {
    field.onChange(null);
  };

  return (
    <CollapsableForm
      title="Custom Size"
      onRemove={handleRemove}
      id={`custom-size-${customPresetId}`}
    >
      <div className="space-y-4">
        <div className="space-y-4">
          {customSizes.map((size: CustomSize, index: number) => (
            <CustomSizeRow
              key={size.id}
              sizeId={size.id}
              sizeLens={customPresetLens.focus(`customSizes.${index}`)}
              size={size}
              showLabels={index === 0}
              canRemove={customSizes.length > 1}
              onChange={(updates: Partial<CustomSize>) => handleSizeChange(size.id, updates)}
              onRemove={() => handleRemoveSize(size.id)}
              bounds={bounds}
              aspectRatio={aspectRatio}
              {...(artworkId && { artworkId })}
              {...(transferTypeId && { transferTypeId })}
              {...(enhancementTier && { enhancementTier })}
            />
          ))}
        </div>

        {/* Add another size button */}
        <button
          type="button"
          onClick={handleAddSize}
          className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          data-testid="add-another-size-button"
        >
          <Plus className="h-4 w-4" />
          Add another size
        </button>
      </div>
    </CollapsableForm>
  );
}
